/**
 * The CONSEQUENCE-NOTICE surface: the bordered, tinted block a screen uses to say "read this before
 * you do the irreversible thing". Exported as a named constant, for the same reason
 * `FEED_CARD_ROOT_CLASS` is: `destructiveNoticeSurface.spec.ts` reads the value from here and measures
 * it out of the token graph, so a one-sided edit goes red instead of shipping two different notices.
 *
 * WHAT THIS EXISTS TO CATCH (`SETTINGS-74`, ux-critique 2026-08-23, fixed 2026-08-24).
 *
 * The recipe used to be `border border-destructive/30 bg-destructive/5`, and `--destructive` is pinned
 * to `--error` and deliberately NEVER redefined in `.dark` (see the note at its declaration in
 * `00-variables.css`). A fixed red at 5% and 30% alpha therefore behaves completely differently over
 * the two card colours, and in dark it stopped being a boundary at all:
 *
 *            boundary vs --card      fill vs --card
 *   light      1.63:1                  1.08:1
 *   dark       1.26:1                  1.02:1        <- 42% of WCAG 1.4.11's 3:1 floor
 *
 * The only surviving dark-theme signal was a 20px `aria-hidden` glyph. On the one settings screen the
 * product calls its only safeguard against an irreversible rename, the four consequences read as an
 * ordinary indented paragraph inside a routine profile form.
 *
 * NO NEW TOKEN WAS NEEDED, and that is the part worth remembering. The row's Direction asked for a
 * dark-specific fill and border to be invented for this recipe. The repo already had one, per-theme by
 * construction and older than the row: `--error-bg` / `--error-border` / `--error-text` and the icon
 * pair, declared in `:root` AND redefined in `.dark`, and already consumed by `Banner.tsx`,
 * `GroupRail.tsx` and `GroupPageSectionLists.tsx`. Measured on this family:
 *
 *            boundary vs --card   vs --background     fill vs --card
 *   light      4.72:1               4.92:1              1.05:1
 *   dark       3.39:1               3.79:1              1.12:1
 *
 * Note what this rules out rather than what it chose: full-strength `--destructive` on `--card` in dark
 * is only 3.16:1, so NO alpha of the destructive token could have cleared the floor with headroom. The
 * error family is not a nicer answer to the same question, it is the only one.
 *
 * The near-flat FILL is correct and is not the defect restated. Elevation here is a border and a tint
 * in a borders-not-shadows language (AGENTS.md rule 23), so the thing that must be measurable is the
 * boundary; a wash that shouted would be a different design language, not a more accessible one.
 *
 * PORT HISTORY. Thirteen further call sites carried the old recipe when this landed (fifteen in all)
 * and could not be touched in the same run because other agents held those files. All are ported now:
 *   metrics (ALERT-10, 2026-09-15): the ten `<Alert variant="destructive">` overrides in
 *     DiscussionMetricsPage, GroupMetricsPage, UserMetricsPage took the EDGE only
 *     (`border-[var(--error-border)]`) and kept their `bg-destructive/5` tint, which
 *     `destructiveTokenContrast.spec.ts` measures at AA under `--destructive-text`. That tint
 *     deliberately diverges from `--error-bg` here.
 *   notice surfaces (FU-1res, 2026-09-15): UserCardDisplayer, RejectJoinRequestDialog, YesNoDialog,
 *     SearchErrorState, SearchPaginatedList consume this constant.
 *   edge-only, own tint kept (FU-1res): DiscussionContentDisplayer's deleted-discussion banner
 *     (`bg-destructive/6` over the card's 3% wash) and StaffCapturedMessages' reported-message card
 *     (`bg-background`).
 * The guard that now pins the inventory lives in `destructiveTokenContrast.spec.ts` ("the border recipe
 * across src/main"): any `border-destructive[/N]` class token under `src/main` fails by file and line.
 */
export const DESTRUCTIVE_NOTICE_SURFACE_CLASS =
    "border border-[var(--error-border)] bg-[var(--error-bg)]";
